import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Menu, Search, Plus, FileDown, Users, MapPin, Building2, Eye, Pencil, Trash2, X,
} from "lucide-react";

export default function AgentClients() {
  const { authAxios, user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [onlyMine, setOnlyMine] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewClient, setViewClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(getEmptyForm());

  function getEmptyForm() {
    return {
      ragione_sociale: "", ragione_sociale_2: "", partita_iva: "",
      indirizzo: "", cap: "", citta: "", provincia: "", nazione: "IT",
      cod_pagamento: "", cod_agente: "", telefono: "", email: "", note: "",
    };
  }

  const fetchClients = useCallback(async () => {
    try {
      const res = await authAxios.get("/clients");
      setClients(res.data);
    } catch { toast.error("Errore caricamento clienti"); }
    finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const cities = useMemo(() => {
    const set = new Set(clients.map(c => c.citta).filter(Boolean));
    return [...set].sort();
  }, [clients]);

  const filteredClients = useMemo(() => {
    const list = clients.filter(c => {
      if (onlyMine && user?.cod_agente && c.cod_agente !== user.cod_agente) return false;
      if (cityFilter !== "all" && c.citta !== cityFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          (c.ragione_sociale || "").toLowerCase().includes(q) ||
          (c.partita_iva || "").toLowerCase().includes(q) ||
          (c.citta || "").toLowerCase().includes(q) ||
          (c.codice_cli || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
    list.sort((a, b) =>
      (a.ragione_sociale || "").localeCompare(b.ragione_sociale || "", "it", { sensitivity: "base" })
    );
    return list;
  }, [clients, searchQuery, cityFilter, onlyMine, user]);

  const openNewForm = () => {
    setEditingClient(null);
    setFormData({ ...getEmptyForm(), cod_agente: user?.cod_agente || "" });
    setShowForm(true);
  };

  const openEditForm = (client) => {
    setEditingClient(client);
    setFormData({
      ragione_sociale: client.ragione_sociale || "",
      ragione_sociale_2: client.ragione_sociale_2 || "",
      partita_iva: client.partita_iva || "",
      indirizzo: client.indirizzo || "",
      cap: client.cap || "",
      citta: client.citta || "",
      provincia: client.provincia || "",
      nazione: client.nazione || "IT",
      cod_pagamento: client.cod_pagamento || "",
      cod_agente: client.cod_agente ?? "",
      telefono: client.telefono || "",
      email: client.email || "",
      note: client.note || "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.ragione_sociale.trim()) {
      toast.error("Ragione sociale obbligatoria");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        cod_agente: formData.cod_agente === "" ? null : parseInt(formData.cod_agente),
      };
      if (editingClient) {
        await authAxios.put(`/clients/${editingClient.id}`, payload);
        toast.success("Cliente aggiornato");
      } else {
        await authAxios.post("/clients", payload);
        toast.success("Cliente proposto creato (standby)");
      }
      setShowForm(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const downloadPdf = async (clientId) => {
    try {
      const res = await authAxios.post(`/clients/${clientId}/pdf`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "scheda_cliente.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch { toast.error("Errore generazione PDF"); }
  };

  const stats = useMemo(() => ({
    total: clients.length,
    mine: user?.cod_agente ? clients.filter(c => c.cod_agente === user.cod_agente).length : 0,
    standby: clients.filter(c => c.status === "standby").length,
    cities: new Set(clients.map(c => c.citta).filter(Boolean)).size,
  }), [clients, user]);

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="agent-clients-page">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="I Miei Clienti" />
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Clienti</h1>
            <p className="text-zinc-400 text-sm">Gestione anagrafica clienti</p>
          </div>
          <Button onClick={openNewForm} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="new-client-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Cliente
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Totale", value: stats.total, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
            { label: "I Miei", value: stats.mine, icon: Building2, color: "text-amber-400", bg: "bg-amber-500/10" },
            { label: "In Standby", value: stats.standby, icon: Plus, color: "text-orange-400", bg: "bg-orange-500/10" },
            { label: "Citta", value: stats.cities, icon: MapPin, color: "text-emerald-400", bg: "bg-emerald-500/10" },
          ].map(s => (
            <Card key={s.label} className="bg-[#18181b] border-white/5">
              <CardContent className="p-3 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.bg}`}>
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <div>
                  <p className={`text-xl font-bold font-mono ${s.color}`}>{s.value}</p>
                  <p className="text-[11px] text-zinc-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Cerca per nome, P.IVA, citta..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10"
              data-testid="client-search-input"
            />
          </div>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="w-[180px] bg-[#18181b] border-white/10" data-testid="city-filter">
              <SelectValue placeholder="Tutte le citta" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-white/10 max-h-60">
              <SelectItem value="all">Tutte le citta</SelectItem>
              {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {user?.cod_agente && (
            <div className="flex items-center gap-2 bg-[#18181b] border border-white/10 rounded-lg px-3 py-2">
              <Switch
                checked={onlyMine}
                onCheckedChange={setOnlyMine}
                data-testid="only-mine-toggle"
              />
              <span className="text-sm text-zinc-300 whitespace-nowrap">Solo i miei</span>
            </div>
          )}
          <Badge variant="outline" className="text-zinc-400 border-white/10">
            {filteredClients.length} risultati
          </Badge>
        </div>

        {/* Clients Table */}
        <Card className="bg-[#18181b] border-white/5">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="py-12 text-center text-zinc-500">
                {clients.length === 0 ? "Nessun cliente importato" : "Nessun risultato trovato"}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-400">Ragione Sociale</TableHead>
                      <TableHead className="text-zinc-400 hidden sm:table-cell">P.IVA</TableHead>
                      <TableHead className="text-zinc-400 hidden md:table-cell">Citta</TableHead>
                      <TableHead className="text-zinc-400 hidden lg:table-cell">Agente</TableHead>
                      <TableHead className="text-zinc-400 text-center">Stato</TableHead>
                      <TableHead className="text-zinc-400 text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.slice(0, 200).map(c => (
                      <TableRow key={c.id} className="border-white/5">
                        <TableCell>
                          <div>
                            <p className="font-medium text-white text-sm">{c.ragione_sociale}</p>
                            {c.ragione_sociale_2 && <p className="text-xs text-zinc-500">{c.ragione_sociale_2}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-zinc-400 text-sm hidden sm:table-cell">{c.partita_iva || "-"}</TableCell>
                        <TableCell className="text-zinc-400 text-sm hidden md:table-cell">
                          {c.citta || "-"}{c.provincia ? ` (${c.provincia})` : ""}
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm hidden lg:table-cell font-mono">{c.cod_agente ?? "-"}</TableCell>
                        <TableCell className="text-center">
                          {c.status === "standby" ? (
                            <Badge className="bg-orange-500/20 text-orange-400 text-[10px]">Standby</Badge>
                          ) : (
                            <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Confermato</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setViewClient(c)} className="text-zinc-400 hover:text-blue-400" data-testid={`view-client-${c.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditForm(c)} className="text-zinc-400 hover:text-amber-400" data-testid={`edit-client-${c.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf(c.id)} className="text-zinc-400 hover:text-emerald-400" data-testid={`pdf-client-${c.id}`}>
                              <FileDown className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredClients.length > 200 && (
                  <div className="p-3 text-center text-zinc-500 text-sm">
                    Mostrando 200 di {filteredClients.length} clienti. Usa i filtri per affinare.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Client Detail Dialog */}
        <Dialog open={!!viewClient} onOpenChange={() => setViewClient(null)}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-400" />
                {viewClient?.ragione_sociale}
              </DialogTitle>
              {viewClient?.status === "standby" && (
                <Badge className="bg-orange-500/20 text-orange-400 w-fit">Proposta - Standby</Badge>
              )}
            </DialogHeader>
            {viewClient && (
              <div className="space-y-3 text-sm">
                {[
                  ["Ragione Sociale 2", viewClient.ragione_sociale_2],
                  ["Partita IVA", viewClient.partita_iva],
                  ["Codice Cliente", viewClient.codice_cli],
                  ["Indirizzo", viewClient.indirizzo],
                  ["CAP", viewClient.cap],
                  ["Citta", viewClient.citta],
                  ["Provincia", viewClient.provincia],
                  ["Nazione", viewClient.nazione],
                  ["Cod. Pagamento", viewClient.cod_pagamento],
                  ["Cod. Agente", viewClient.cod_agente],
                  ["Telefono", viewClient.telefono],
                  ["Email", viewClient.email],
                  ["Note", viewClient.note],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-zinc-400">{label}</span>
                    <span className="text-white font-medium text-right max-w-[60%]">{value}</span>
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { downloadPdf(viewClient?.id); }} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                <FileDown className="w-4 h-4 mr-2" /> Scarica PDF
              </Button>
              <Button variant="outline" onClick={() => setViewClient(null)} className="border-white/10">Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Client Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingClient ? "Modifica Cliente" : "Proponi Nuovo Cliente"}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {editingClient ? "Modifica i dati del cliente" : "Il cliente verra creato in stato standby fino alla conferma dell'admin"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-300 text-xs">Ragione Sociale *</Label>
                <Input value={formData.ragione_sociale} onChange={e => setFormData(p => ({ ...p, ragione_sociale: e.target.value }))}
                  className="bg-[#27272a] border-white/10" data-testid="form-ragione-sociale" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Ragione Sociale 2</Label>
                <Input value={formData.ragione_sociale_2} onChange={e => setFormData(p => ({ ...p, ragione_sociale_2: e.target.value }))}
                  className="bg-[#27272a] border-white/10" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Partita IVA</Label>
                  <Input value={formData.partita_iva} onChange={e => setFormData(p => ({ ...p, partita_iva: e.target.value }))}
                    className="bg-[#27272a] border-white/10" data-testid="form-partita-iva" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Cod. Agente</Label>
                  <Input type="number" value={formData.cod_agente} onChange={e => setFormData(p => ({ ...p, cod_agente: e.target.value }))}
                    className="bg-[#27272a] border-white/10" data-testid="form-cod-agente" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Indirizzo</Label>
                <Input value={formData.indirizzo} onChange={e => setFormData(p => ({ ...p, indirizzo: e.target.value }))}
                  className="bg-[#27272a] border-white/10" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">CAP</Label>
                  <Input value={formData.cap} onChange={e => setFormData(p => ({ ...p, cap: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Citta</Label>
                  <Input value={formData.citta} onChange={e => setFormData(p => ({ ...p, citta: e.target.value }))}
                    className="bg-[#27272a] border-white/10" data-testid="form-citta" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Prov.</Label>
                  <Input value={formData.provincia} onChange={e => setFormData(p => ({ ...p, provincia: e.target.value }))}
                    className="bg-[#27272a] border-white/10" maxLength={2} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Telefono</Label>
                  <Input value={formData.telefono} onChange={e => setFormData(p => ({ ...p, telefono: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Email</Label>
                  <Input value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Cod. Pagamento</Label>
                  <Input value={formData.cod_pagamento} onChange={e => setFormData(p => ({ ...p, cod_pagamento: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Nazione</Label>
                  <Input value={formData.nazione} onChange={e => setFormData(p => ({ ...p, nazione: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Note</Label>
                <Input value={formData.note} onChange={e => setFormData(p => ({ ...p, note: e.target.value }))}
                  className="bg-[#27272a] border-white/10" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-white/10">Annulla</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-client-btn">
                {saving ? "Salvataggio..." : editingClient ? "Salva Modifiche" : "Proponi Cliente"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
